import { createParkRequest } from '@/services/request';
import type {
  CreateServiceApplicationParams,
  EvaluateServiceApplicationParams,
  ServiceApplicationDetail,
  ServiceApplicationEvaluation,
  ServiceApplicationFileInput,
  ServiceApplicationListParams,
  ServiceMatterCategory,
  ServiceMatterDetail,
  ServiceMatterListParams,
  ServiceMatterPageResult,
  ServiceMatterSummary,
  ServiceApplicationSummary,
  SupplementServiceApplicationParams,
} from './types/service-matter';

const parkRequest = createParkRequest();

export const listEnterpriseServiceMatterCategories = () =>
  parkRequest<{ items: ServiceMatterCategory[] }>(
    '/bff/enterprise/service-matter-categories',
    { method: 'GET' },
  );

export const listEnterpriseServiceMatters = (
  params?: ServiceMatterListParams,
) =>
  parkRequest<ServiceMatterPageResult<ServiceMatterSummary>>(
    '/bff/enterprise/service-matters',
    { method: 'GET', params },
  );

export const getEnterpriseServiceMatter = (uuid: string) =>
  parkRequest<ServiceMatterDetail>(`/bff/enterprise/service-matters/${uuid}`, {
    method: 'GET',
  });

export const listEnterpriseServiceApplications = (
  params?: ServiceApplicationListParams,
) =>
  parkRequest<ServiceMatterPageResult<ServiceApplicationSummary>>(
    '/bff/enterprise/service-applications',
    {
      method: 'GET',
      params,
    },
  );

export const getEnterpriseServiceApplication = (uuid: string) =>
  parkRequest<ServiceApplicationDetail>(
    `/bff/enterprise/service-applications/${uuid}`,
    { method: 'GET' },
  );

export const createEnterpriseServiceApplication = (
  data: CreateServiceApplicationParams,
) =>
  parkRequest<ServiceApplicationDetail>(
    '/bff/enterprise/service-applications',
    { method: 'POST', data },
  );

export const supplementEnterpriseServiceApplication = (
  uuid: string,
  data: SupplementServiceApplicationParams,
) =>
  parkRequest<ServiceApplicationDetail>(
    `/bff/enterprise/service-applications/${uuid}/supplement`,
    { method: 'POST', data },
  );

export const uploadEnterpriseServiceFile = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('scene', 'service_file');
  return parkRequest<{
    uuid: string;
    fileName: string;
    fileSize: number;
    mimeType?: string;
  }>('/bff/files', {
    method: 'POST',
    data: formData,
    requestType: 'form',
  });
};

export const getEnterpriseServiceFile = (fileUuid: string) =>
  parkRequest<{
    uuid: string;
    fileName: string;
    fileSize?: number;
    mimeType?: string;
    url: string;
  }>(`/bff/files/${fileUuid}`, {
    method: 'GET',
  });

export const cancelEnterpriseServiceApplication = (uuid: string) =>
  parkRequest<ServiceApplicationDetail>(
    `/bff/enterprise/service-applications/${uuid}/cancel`,
    { method: 'POST' },
  );

export const confirmEnterpriseServiceApplication = (
  uuid: string,
  data?: { remark?: string },
) =>
  parkRequest<ServiceApplicationDetail>(
    `/bff/enterprise/service-applications/${uuid}/confirm`,
    { method: 'POST', data: data || {} },
  );

export const disputeEnterpriseServiceApplication = (
  uuid: string,
  data: { remark: string; attachments?: ServiceApplicationFileInput[] },
) =>
  parkRequest<ServiceApplicationDetail>(
    `/bff/enterprise/service-applications/${uuid}/dispute`,
    { method: 'POST', data },
  );

export const getEnterpriseServiceApplicationEvaluation = (uuid: string) =>
  parkRequest<ServiceApplicationEvaluation | null>(
    `/bff/enterprise/service-applications/${uuid}/evaluation`,
    { method: 'GET' },
  );

export const evaluateEnterpriseServiceApplication = (
  uuid: string,
  data: EvaluateServiceApplicationParams,
) =>
  parkRequest<ServiceApplicationDetail>(
    `/bff/enterprise/service-applications/${uuid}/evaluation`,
    { method: 'POST', data },
  );
